import React, { useCallback, useState, useEffect } from "react";
import { Grid, Box, Typography, Paper } from "@mui/material";
import { intl, DmsPage, fetchData, HTTP_STATUS } from "dmsbasereact";
import { DmsAnalytics, useDmsAnalyticsContext } from "../../components/DmsAnalytics";

const ProductionBody = () => {
    const { queryParams, refreshKey } = useDmsAnalyticsContext();
    const [productionSummary, setProductionSummary] = useState({
        total_produced: 0,
        total_in_stock: 0,
        total_distributed: 0,
    });
    const [loadingProduction, setLoadingProduction] = useState(false);

    const loadProductionSummary = useCallback(async () => {
        setLoadingProduction(true);
        try {
            const response = await fetchData(
                `/analytics/production-summary?${queryParams}`,
                "GET",
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                setProductionSummary(response.data?.data || {
                    total_produced: 0,
                    total_in_stock: 0,
                    total_distributed: 0,
                });
            }
        } catch (err) {
            console.error("Error loading production summary:", err);
        } finally {
            setLoadingProduction(false);
        }
    }, [queryParams]);

    useEffect(() => {
        loadProductionSummary();
    }, [loadProductionSummary, refreshKey]);

    return (
        <>
            {/* Production Summary Cards */}
            <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "primary.main" }}>
                            <Typography variant="h6" color="primary.contrastText">
                                {intl.get("Analytics.Production.TotalProduced").d("Total Produzido")}
                            </Typography>
                            <Typography variant="h4" color="primary.contrastText">
                                {loadingProduction ? "..." : productionSummary.total_produced?.toLocaleString("pt-BR") || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "success.main" }}>
                            <Typography variant="h6" color="success.contrastText">
                                {intl.get("Analytics.Production.InStock").d("Em Estoque (CD)")}
                            </Typography>
                            <Typography variant="h4" color="success.contrastText">
                                {loadingProduction ? "..." : productionSummary.total_in_stock?.toLocaleString("pt-BR") || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "info.main" }}>
                            <Typography variant="h6" color="info.contrastText">
                                {intl.get("Analytics.Production.Distributed").d("Distribuido")}
                            </Typography>
                            <Typography variant="h4" color="info.contrastText">
                                {loadingProduction ? "..." : productionSummary.total_distributed?.toLocaleString("pt-BR") || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Bar
                        url="/analytics/production/by-product"
                        title={intl.get("Analytics.Production.ByProduct").d("Produzido por Produto")}
                        crossFilter="product"
                        color="primary"
                        labelKey="name"
                        valueKey="value"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Bar
                        url="/analytics/production/by-batch"
                        title={intl.get("Analytics.Production.ByBatch").d("Produzido por Lote")}
                        crossFilter="batch"
                        color="warning"
                        labelKey="name"
                        valueKey="value"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Table
                        url="/analytics/stock/by-product"
                        title={intl.get("DmsAnalytics.Stock.ByProduct").d("Unidades Produzidas e Estoque por Produto")}
                        columns={[
                            { field: "code", header: intl.get("DmsAnalytics.Stock.ProductCode").d("Product Code") },
                            { field: "name", header: intl.get("DmsAnalytics.Stock.Product").d("Produto") },
                            { field: "produced", header: intl.get("DmsAnalytics.Stock.Produced").d("Unidades Produzidas"), align: "right" },
                            { field: "remaining", header: intl.get("DmsAnalytics.Stock.Remaining").d("Estoque Remanescente"), align: "right" },
                        ]}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <DmsAnalytics.Table
                        url="/analytics/stock/by-batch"
                        title={intl.get("DmsAnalytics.Stock.ByBatch").d("Unidades Produzidas e Estoque por Lote")}
                        columns={[
                            { field: "name", header: intl.get("DmsAnalytics.Stock.Batch").d("Lote") },
                            { field: "produced", header: intl.get("DmsAnalytics.Stock.Produced").d("Unidades Produzidas"), align: "right" },
                            { field: "remaining", header: intl.get("DmsAnalytics.Stock.Remaining").d("Estoque Remanescente"), align: "right" },
                        ]}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export const ProductionAnalyticsPage = () => {
    return (
        <DmsPage name={intl.get("Analytics.Production.Title").d("Producao Eurofarma")} manual_slug="analytics-production">
            <DmsAnalytics.Card>
                <DmsAnalytics.Header title={intl.get("Analytics.Production.Title").d("Producao Eurofarma")} />
                <DmsAnalytics.Chips />
                <DmsAnalytics.Body>
                    <ProductionBody />
                </DmsAnalytics.Body>
                <DmsAnalytics.Filters />
            </DmsAnalytics.Card>
        </DmsPage>
    );
};
